"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCarousel = void 0;
const react_1 = require("react");
const ui_1 = require("@phishtool/ui");
const styled_1 = require("./styled");
const stepIntervalMs = 4000;
const snapResetMs = 50;
const reducedMotionQuery = '(prefers-reduced-motion: reduce)';
const smallQuery = `(min-width: ${ui_1.smallBreakpoint}px)`;
const mediumQuery = `(min-width: ${ui_1.mediumBreakpoint}px)`;
const getVisibleCount = () => {
    if (window.matchMedia(mediumQuery).matches) {
        return styled_1.visibleCounts[2];
    }
    if (window.matchMedia(smallQuery).matches) {
        return styled_1.visibleCounts[1];
    }
    return styled_1.visibleCounts[0];
};
const useCarousel = ({ itemCount }) => {
    const [visibleCount, setVisibleCount] = (0, react_1.useState)(null);
    const [isReducedMotion, setIsReducedMotion] = (0, react_1.useState)(false);
    const [index, setIndex] = (0, react_1.useState)(0);
    const [isPaused, setIsPaused] = (0, react_1.useState)(false);
    const [isHidden, setIsHidden] = (0, react_1.useState)(false);
    const [isSnapping, setIsSnapping] = (0, react_1.useState)(false);
    // Read after mount only, so the server render and the first client render agree
    (0, react_1.useEffect)(() => {
        const queries = [smallQuery, mediumQuery, reducedMotionQuery].map((query) => window.matchMedia(query));
        const sync = () => {
            setVisibleCount(getVisibleCount());
            setIsReducedMotion(window.matchMedia(reducedMotionQuery).matches);
            setIndex(0);
        };
        sync();
        queries.forEach((query) => query.addEventListener('change', sync));
        return () => queries.forEach((query) => query.removeEventListener('change', sync));
    }, []);
    // Backgrounded tabs throttle timers and stall transitions, so don't advance while hidden.
    // Seeded on mount too — a tab opened in the background never fires visibilitychange.
    (0, react_1.useEffect)(() => {
        const sync = () => setIsHidden(document.hidden);
        sync();
        document.addEventListener('visibilitychange', sync);
        return () => document.removeEventListener('visibilitychange', sync);
    }, []);
    const canScroll = visibleCount !== null && !isReducedMotion && itemCount > visibleCount;
    (0, react_1.useEffect)(() => {
        if (!canScroll || isPaused || isHidden) {
            return;
        }
        const intervalId = setInterval(() => setIndex((current) => current + 1), stepIntervalMs);
        return () => clearInterval(intervalId);
    }, [canScroll, isPaused, isHidden]);
    // Once a step lands on the duplicated half, jump back by a full list with the transition off.
    // Timed off the transition rather than listening for transitionend: a backgrounded or occluded
    // tab suspends rendering, so that event may never arrive and the index would run away.
    (0, react_1.useEffect)(() => {
        if (index < itemCount) {
            return;
        }
        const timeoutId = setTimeout(() => {
            setIsSnapping(true);
            setIndex((current) => current - itemCount);
        }, styled_1.transitionMs);
        return () => clearTimeout(timeoutId);
    }, [index, itemCount]);
    // Re-arm the transition once the untransitioned snap has painted. A frame is the accurate
    // signal, but requestAnimationFrame never runs in a backgrounded tab, so a timer backstops it
    // — without one the carousel stays stuck with animation off for the rest of the session.
    (0, react_1.useEffect)(() => {
        if (!isSnapping) {
            return;
        }
        let innerFrame = 0;
        const outerFrame = requestAnimationFrame(() => {
            innerFrame = requestAnimationFrame(() => setIsSnapping(false));
        });
        const timeoutId = setTimeout(() => setIsSnapping(false), snapResetMs);
        return () => {
            cancelAnimationFrame(outerFrame);
            cancelAnimationFrame(innerFrame);
            clearTimeout(timeoutId);
        };
    }, [isSnapping]);
    const pause = (0, react_1.useCallback)(() => setIsPaused(true), []);
    const resume = (0, react_1.useCallback)(() => setIsPaused(false), []);
    return {
        canScroll,
        offset: canScroll && visibleCount ? index * (100 / visibleCount) : 0,
        isAnimated: canScroll && !isSnapping,
        pause,
        resume,
    };
};
exports.useCarousel = useCarousel;
